/**
 * HTTP client and wire types for Flux Agent's custom views (`/flux-agent/views`). Every call goes
 * through the shared credentialed `request()` helper and folds into a `FluxAgentResult`. The
 * server owns every rule: ownership, the allowed data list, consent per revision and shaping.
 *
 * @module apps/web/src/lib/fluxViewsApi
 */

import { request } from '@/lib/api';
import { currentTimeZone, toFluxAgentResult, type FluxAgentResult } from '@/lib/fluxAgentApi';

/** Read-only data a view may declare, as named by the server. */
export type FluxViewSource = 'week_activities' | 'backlog' | 'values' | 'recent_sessions';

/** Plain-language label of each source, shown on the consent screen and in the list. */
export const VIEW_SOURCE_LABELS: Record<FluxViewSource, string> = {
  week_activities: "This week's activities",
  backlog: 'Your backlog (unscheduled activities)',
  values: 'Your values',
  recent_sessions: 'Sessions from the last 14 days',
};

/** Whether a view may receive its data. */
export interface FluxViewAccess {
  /** The view declared data, so the person must allow it once per revision. */
  needed: boolean;
  /** The person allowed every declared source for the current revision. */
  granted: boolean;
}

/** A stored custom view. */
export interface FluxView {
  /** Server id, `view_…`. */
  id: string;
  /** Conversation it was made in, or null once that conversation is deleted. */
  conversationId: string | null;
  /** Heading on the card and in the list. */
  title: string;
  /** One line saying what it helps with. */
  purpose: string;
  /** TSX source; only present on `getView`. */
  source?: string;
  /** Declared read-only data. */
  sources: FluxViewSource[];
  /** The list the person allowed, or null before they allowed anything. */
  approvedSources: FluxViewSource[] | null;
  /** Consent state for the current revision. */
  access: FluxViewAccess;
  /** Increments on each revision; consent names the revision it was given for. */
  revision: number;
  /** UTC creation time as stored by D1. */
  createdAt: string;
  /** UTC time of the latest revision or rename. */
  updatedAt: string;
}

/** Approved data for one open of a view. */
export interface FluxViewData {
  /** Shaped rows keyed by source. */
  data: Record<string, unknown>;
  /** True when rows were dropped to fit the payload limit. */
  truncated: boolean;
  /** When the data was read, in the person's timezone. */
  generatedAt: string;
  /** The revision the data was served for. */
  revision: number;
}

/** Send a request to `/flux-agent/views{path}` and fold the envelope. */
async function call<T>(path: string, options: RequestInit = {}): Promise<FluxAgentResult<T>> {
  return toFluxAgentResult(await request<T>(`/flux-agent/views${path}`, options));
}

/** Client calls for the views routes. */
export const fluxViewsApi = {
  /** The caller's 100 most recently changed views, without source. */
  listViews: () => call<{ views: FluxView[] }>(''),
  /** One view with its source. */
  getView: (id: string) => call<{ view: FluxView }>(`/${encodeURIComponent(id)}`),
  /** Rename a view (1–80 characters, one line). */
  renameView: (id: string, title: string) =>
    call<{ view: FluxView }>(`/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),
  /** Delete a view. */
  deleteView: (id: string) =>
    call<{ deleted: boolean }>(`/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  /** Allow exactly the declared `sources` for `revision`, or revoke with `allow: false`. */
  setAccess: (id: string, revision: number, allow: boolean, sources: FluxViewSource[]) =>
    call<{ view: FluxView }>(`/${encodeURIComponent(id)}/access`, {
      method: 'POST',
      body: JSON.stringify({ allow, revision, sources }),
    }),
  /** Binding: request approved data for the exact component revision being opened. */
  getData: (id: string, revision: number) =>
    call<FluxViewData>(
      `/${encodeURIComponent(id)}/data?revision=${encodeURIComponent(revision)}&timezone=${encodeURIComponent(currentTimeZone())}`
    ),
};
